import {
  ITeams,
  IMatch,
  IPlayer,
  IStaff,
  IPendingGameParticipant,
  IGameRecord,
} from "../../../types/types";
import { injectCurrentSeasonIfNeeded } from "../../../config/currentSeason";
import { parseApiResponse } from "../../../services/utils/apiError";
import { normalizeGameRecord } from "../../../utils/lolAssets";
import { IGameSupplementBatchPayload } from "../forms/gameSupplementPayload";
export type Position = "TOP" | "JUNGLE" | "MID" | "BOTTOM" | "SUPPORT" | "FILL";
export type PlayerType = "MAIN" | "SUBSTITUTE";
export type MatchType = "REGULAR" | "PLAYOFF" | "RELEGATION";
export type UserType = "ADMIN" | "DEFAULT" | "SUPER";

export interface GameID {
  game_id: string | number;
}

export interface IMatchGamesPayload {
  match_id: number;
  game_ids: number[];
}

export type MatchFormSubmit = Partial<IMatch> & { gameIds?: string[] };

const api = import.meta.env.VITE_API_URL;

function prepareMutationBody(method: "POST" | "PUT" | "DELETE", data: unknown): unknown {
  if (method === "POST" || method === "PUT") {
    return injectCurrentSeasonIfNeeded(data);
  }
  return data;
}

async function fetchMutation(
  endpoint: string,
  method: "POST" | "PUT" | "DELETE",
  data: unknown,
): Promise<void> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const response = await fetch(`${api}${endpoint}`, {
    method,
    headers,
    credentials: "include",
    body: JSON.stringify(prepareMutationBody(method, data)),
  });

  await parseApiResponse<unknown>(response);
}

export const teamAdminApi = {
  updateTeam: (payload: ITeams) => fetchMutation("/teams", "PUT", payload),
  createTeam: (payload: ITeams) => fetchMutation("/teams", "POST", payload),
  deleteTeam: (id: string) => fetchMutation("/teams", "DELETE", { id }),
};

export const fetchMatchGames = async (): Promise<IGameRecord[]> => {
  const response = await fetch(`${api}/games`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  const games = (await parseApiResponse<IGameRecord[]>(response)) ?? [];
  return games.map((game) => normalizeGameRecord(game));
};

export const fetchGameRecordsByIds = async (
  gameIds: string[],
): Promise<Map<string, IGameRecord>> => {
  if (gameIds.length === 0) return new Map();

  const games = await fetchMatchGames();
  const wanted = new Set(gameIds.map((id) => id.trim()).filter(Boolean));
  const records = new Map<string, IGameRecord>();

  for (const game of games) {
    const id = String(game.game_id);
    if (wanted.has(id)) {
      records.set(id, game);
    }
  }

  return records;
};

export const matchAdminApi = {
  updateMatch: (payload: IMatch) => fetchMutation("/matches", "PUT", payload),
  createMatch: async (payload: Omit<IMatch, "id">): Promise<string | undefined> => {
    const response = await fetch(`${api}/matches`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(prepareMutationBody("POST", payload)),
    });
    return (await parseApiResponse<string>(response)) ?? undefined;
  },
  postMatchGameIDs: (payload: IMatchGamesPayload) => fetchMutation("/game", "POST", payload),
  patchGameSupplements: async (payload: IGameSupplementBatchPayload): Promise<void> => {
    const response = await fetch(`${api}/game/supplement`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    await parseApiResponse<unknown>(response);
  },
  deleteMatch: (id: string) => fetchMutation("/matches", "DELETE", { id }),
};

export const playerAdminApi = {
  updatePlayer: (payload: IPlayer) => fetchMutation("/players", "PUT", payload),
  createPlayer: (payload: Omit<IPlayer, "id">) => fetchMutation("/players", "POST", payload),
  deletePlayer: (id: string) => fetchMutation("/players", "DELETE", { id }),
};

export const staffAdminApi = {
  updateStaff: (payload: IStaff) => fetchMutation("/staff", "PUT", payload),
  createStaff: (payload: Omit<IStaff, "id">) => fetchMutation("/staff", "POST", payload),
  deleteStaff: (id: string) => fetchMutation("/staff", "DELETE", { id }),
};

export const fetchPendingParticipants = async (): Promise<IPendingGameParticipant[]> => {
  const response = await fetch(`${api}/season-stats/pending-participants?status=pending`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });
  return (await parseApiResponse<IPendingGameParticipant[]>(response)) ?? [];
};

export const assignPendingParticipant = (pendingId: number, playerId: string) =>
  fetchMutation("/season-stats/pending-participants/assign", "POST", { pendingId, playerId });

export const dismissPendingParticipant = (pendingId: number) =>
  fetchMutation("/season-stats/pending-participants/dismiss", "POST", { pendingId });
